import { supabase } from './supabase'
import type { Activity, Comment, PortfolioFile, Profile, Submission, Visibility } from '../types'

const colors = ['#3b82f6','#f97316','#14b8a6','#ec4899','#8b5cf6','#eab308']
const profileFromDb = (p: any): Profile => ({ id: p.id, displayName: p.display_name, role: p.role, avatarColor: p.avatar_color || colors[0] })
const activityFromDb = (a: any): Activity => ({ id:a.id, title:a.title, description:a.description, emoji:a.emoji, subject:a.subject, visibility:a.visibility, dueDate:a.due_date || undefined, createdAt:a.created_at })

export async function loadClassroom() {
  if (!supabase) throw new Error('Supabase가 연결되지 않았어요.')
  const [profilesResult, activitiesResult, submissionsResult, userResult] = await Promise.all([
    supabase.from('profiles').select('*').order('display_name'),
    supabase.from('activities').select('*').order('created_at', { ascending:false }),
    supabase.from('submissions').select(`*,owner:profiles!owner_id(*),files:submission_files(*),comments(*,author:profiles!author_id(*)),reactions(*)`).order('created_at',{ascending:false}),
    supabase.auth.getUser(),
  ])
  const error = profilesResult.error || activitiesResult.error || submissionsResult.error
  if (error) throw error
  const submissions: Submission[] = (submissionsResult.data || []).map((s:any) => ({
    id:s.id, owner:profileFromDb(s.owner), activityId:s.activity_id || undefined, title:s.title,
    reflection:s.reflection, cover:coverFor(s.id), createdAt:new Date(s.created_at).toLocaleDateString('ko-KR'),
    visibility:s.visibility, files:(s.files || []).map((f:any):PortfolioFile => ({ id:f.id,name:f.original_name,type:f.mime_type,size:f.size_bytes })),
    praises:(s.reactions || []).reduce((out:any,r:any) => ({...out,[r.kind]:(out[r.kind]||0)+1}),{}),
    myPraises:(s.reactions || []).filter((r:any) => r.author_id === userResult.data.user?.id).map((r:any) => r.kind),
    comments:(s.comments || []).sort((a:any,b:any) => a.created_at.localeCompare(b.created_at)).map((c:any):Comment => ({ id:c.id,author:profileFromDb(c.author),body:c.body,createdAt:new Date(c.created_at).toLocaleDateString('ko-KR'),isTeacher:c.author.role==='teacher' })),
  }))
  return {
    profiles:(profilesResult.data || []).map(profileFromDb),
    activities:(activitiesResult.data || []).map(activityFromDb),
    submissions,
  }
}

function coverFor(seed:string) {
  const covers = [
    'linear-gradient(135deg,#a7d8ff 0%,#e8f4ff 52%,#7ec8a5 52%,#54a77c 100%)',
    'linear-gradient(145deg,#d8f3dc 0%,#95d5b2 55%,#52b788 55%,#2d6a4f 100%)',
    'linear-gradient(135deg,#fee2b3 0%,#ffd166 50%,#ef8354 50%,#d95d39 100%)',
  ]
  return covers[seed.charCodeAt(0) % covers.length]
}

export async function createSubmission(owner:Profile, input:{activityId?:string,title:string,reflection:string,visibility:Visibility,files:File[]}) {
  if (!supabase) throw new Error('Supabase가 연결되지 않았어요.')
  const { data:row,error } = await supabase.from('submissions').insert({ owner_id:owner.id, activity_id:input.activityId || null, title:input.title, reflection:input.reflection, visibility:input.visibility }).select().single()
  if (error) throw error
  try {
    for (const file of input.files) {
      const safeName = file.name.replace(/[^a-zA-Z0-9가-힣._-]/g,'_')
      const path = `${owner.id}/${row.id}/${crypto.randomUUID()}-${safeName}`
      const uploaded = await supabase.storage.from('portfolio-files').upload(path,file,{contentType:file.type || 'application/octet-stream'})
      if (uploaded.error) throw uploaded.error
      const meta = await supabase.from('submission_files').insert({ submission_id:row.id,storage_path:path,original_name:file.name,mime_type:file.type || 'application/octet-stream',size_bytes:file.size })
      if (meta.error) throw meta.error
    }
  } catch (error) { await supabase.from('submissions').delete().eq('id',row.id); throw error }
  return row.id as string
}

export async function getFileUrl(fileId:string) {
  if (!supabase) return null
  const { data:meta,error } = await supabase.from('submission_files').select('storage_path').eq('id',fileId).single()
  if (error) throw error
  const signed = await supabase.storage.from('portfolio-files').createSignedUrl(meta.storage_path,900)
  if (signed.error) throw signed.error
  return signed.data.signedUrl
}

// Keep the previous object in storage so replacing a file is recoverable by the teacher.
export async function replaceSubmissionFile(fileId:string, file:File) {
  if (!supabase) throw new Error('Supabase가 연결되지 않았어요.')
  if (file.size > 52428800) throw new Error('파일은 50MB 이하로 골라 주세요.')
  const {data:user,error:authError} = await supabase.auth.getUser()
  if (authError || !user.user) throw new Error('다시 로그인해 주세요.')
  const path = `${user.user.id}/revisions/${crypto.randomUUID()}.${file.name.split('.').pop()?.replace(/[^a-zA-Z0-9]/g,'') || 'bin'}`
  const mime = file.type || 'application/octet-stream'
  const upload = await supabase.storage.from('portfolio-files').upload(path,file,{contentType:mime})
  if (upload.error) throw upload.error
  const result = await supabase.from('submission_files').update({storage_path:path,original_name:file.name,mime_type:mime,size_bytes:file.size}).eq('id',fileId).select('id').single()
  if (result.error) throw result.error
}

export async function addComment(submissionId:string, authorId:string, body:string) {
  if (!supabase) return
  const { error } = await supabase.from('comments').insert({submission_id:submissionId,author_id:authorId,body})
  if (error) throw error
}

export async function togglePraise(submissionId:string, authorId:string, kind:string, active:boolean) {
  if (!supabase) return
  const query = active
    ? supabase.from('reactions').delete().eq('submission_id',submissionId).eq('author_id',authorId).eq('kind',kind)
    : supabase.from('reactions').insert({submission_id:submissionId,author_id:authorId,kind})
  const { error } = await query
  if (error) throw error
}

export async function deleteSubmission(id:string) {
  if (!supabase) return
  const { data:files } = await supabase.from('submission_files').select('storage_path').eq('submission_id',id)
  if (files?.length) await supabase.storage.from('portfolio-files').remove(files.map(f => f.storage_path))
  const { error } = await supabase.from('submissions').delete().eq('id',id)
  if (error) throw error
}

export async function updateSubmission(id:string, values:{title:string;reflection:string;visibility:Visibility}) {
  if (!supabase) return
  const { error } = await supabase.from('submissions').update({...values,updated_at:new Date().toISOString()}).eq('id',id)
  if (error) throw error
}

export async function saveActivity(input:Partial<Activity> & {title:string;createdBy:string}) {
  if (!supabase) return
  const values = {title:input.title,description:input.description||'',emoji:input.emoji||'📚',subject:input.subject||'기타',visibility:input.visibility||'class',due_date:input.dueDate||null,created_by:input.createdBy}
  const { error } = input.id ? await supabase.from('activities').update(values).eq('id',input.id) : await supabase.from('activities').insert(values)
  if (error) throw error
}

export async function removeActivity(id:string) {
  if (!supabase) return
  const { error } = await supabase.from('activities').delete().eq('id',id)
  if (error) throw error
}

export async function createStudent(name:string,pin:string) {
  if (!supabase) return
  const { error } = await supabase.functions.invoke('manage-member',{body:{action:'create',name,pin}})
  if (error) throw error
}

export async function resetStudentPin(userId:string,pin:string) {
  if (!supabase) return
  const { error } = await supabase.functions.invoke('manage-member',{body:{action:'reset-pin',userId,pin}})
  if (error) throw error
}

export async function deleteStudent(userId:string) {
  if (!supabase) return
  const { error } = await supabase.functions.invoke('manage-member',{body:{action:'delete',userId}})
  if (error) throw error
}
